import React, { useMemo, useEffect } from "react";
import { Modal } from "react-bootstrap";
import useForm from "../app/hooks/useForm";
import PasswordInput from "../comman/PasswordInput";
import http from "../services/HttpService";
import auth from "../services/authService";
import config from "../config.json";
import Joi from "joi-browser";
import { getAlert } from "../services/appAlerts";

const ChangePassword = ({ showHide, handleModalShowHide }) => {
  const schema = useMemo(
    () => ({
      oldPassword: Joi.string().label("Existing Password"),
      newPassword: Joi.string().label("New Password"),
      confirmPassword: Joi.any()
        .valid(Joi.ref("newPassword"))
        .required()
        .options({ language: { any: { allowOnly: "must match new password" } } })
        .label("Confirm Password"),
    }),
    []
  );

  const passwordValidate = (password) => {
    var re = {
      capital: /[A-Z]/,
      digit: /[0-9]/,
      except: /[aeiou]/,
      full: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?][A-Za-z0-9]/,
    };
    return (
      re.capital.test(password) &&
      re.digit.test(password) &&
      re.except.test(password) &&
      re.full.test(password)
    );
  };

  const doSubmit = async () => {
    try {
      const passResult = passwordValidate(stateData.newPassword);
      if (passResult) {
        const user = auth.getCurrentUser();
        const payload = {
          oldPassword: stateData.oldPassword,
          newPassword: stateData.newPassword,
          confirmPassword: stateData.confirmPassword,
          userId: user?.id,
        };
        await http.post(config.ChangePassword, payload);
        getAlert("", "Password Changed Successfully", "success");
      } else {
        getAlert(
          "Password Strength",
          "Please enter strong password",
          "error"
        );
      }
    } catch (ex) {
      const errors = { ...stateErrors };
      errors["oldPassword"] = ex?.response?.data?.message;
      setStateErrors(errors);
    }
  };

  const {
    stateData,
    setStateData,
    stateErrors,
    setStateErrors,
    handleChange,
    validate,
    handleSubmit,
  } = useForm(schema, doSubmit);

  useEffect(() => {
    if (showHide) {
      setStateData({});
      setStateErrors({});
    }
  }, [showHide, setStateData, setStateErrors]);

  const renderInput = (name, label, icon) => {
    return (
      <PasswordInput
        name={name}
        value={stateData[name] || ""}
        label={label}
        onChange={handleChange}
        error={stateErrors[name]}
        req={schema[name]}
        icon={icon}
      />
    );
  };

  const user = auth.getCurrentUser();
  const newPassword = stateData.newPassword || "";
  const confirmPassword = stateData.confirmPassword || "";

  // Dynamic validation checks matching the password strength requirements
  const isLengthValid = newPassword.length >= 8 && newPassword.length <= 50;
  const isCapitalValid = /[A-Z]/.test(newPassword);
  const isVowelValid = /[aeiou]/.test(newPassword);
  const isDigitValid = /[0-9]/.test(newPassword);
  const isSpecialValid = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?][A-Za-z0-9]/.test(newPassword);
  const isMatchValid = confirmPassword.length > 0 && confirmPassword === newPassword;
  const hasTyped = newPassword.length > 0;

  const renderCheckIcon = (isValid) => {
    if (!hasTyped) {
      return <i className="fa fa-circle-o text-muted" aria-hidden="true" />;
    }
    return isValid ? (
      <i className="fa fa-check-circle text-success" aria-hidden="true" />
    ) : (
      <i className="fa fa-times-circle text-danger" aria-hidden="true" />
    );
  };

  return (
    <React.Fragment>
      <Modal
        show={showHide}
        onHide={handleModalShowHide}
        dialogClassName="rt-profile-modal theme-primary"
        centered
        backdrop="static"
      >
        <style>{`

        /* Ensure the modal and backdrop are on top of sidebars/headers */
        .modal-backdrop {
          z-index: 1080 !important;
        }
        .modal {
          z-index: 1090 !important;
        }

        .rt-profile-modal {
          width: min(1100px, calc(100vw - 64px)) !important;
          max-width: min(1100px, calc(100vw - 64px)) !important;
          height: auto !important;
        }
        .rt-profile-modal .modal-content {
          border-radius: 0 !important;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1) !important;
          border: 1px solid #cbd5e1 !important;
          overflow: hidden !important;
          background: #ffffff !important;
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: calc(100vh - 60px) !important;
          display: flex !important;
          flex-direction: column !important;
          font-family: inherit !important;
        }
        .rt-profile-modal .modal-header {
          position: relative !important;
          padding: 16px 20px !important;
          background: #ffffff !important;
          border-bottom: 1px solid #e2e8f0 !important;
          border-radius: 0 !important;
        }
        .rt-profile-modal .modal-header::before {
          display: none !important;
        }
        .rt-profile-modal .rt-header-shell {
          width: 100% !important;
        }
        .rt-profile-modal .rt-header-top {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          gap: 12px !important;
        }
        .rt-profile-modal .rt-header-main {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
          min-width: 0 !important;
          flex: 1 !important;
        }
        .rt-profile-modal .rt-header-copy {
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          min-width: 0 !important;
        }
        .rt-profile-modal .rt-header-icon {
          width: 52px !important;
          height: 52px !important;
          min-height: 52px !important;
          border-radius: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #171717 !important;
          background: #f5f5f5 !important;
          border: 1px solid #d4d4d4 !important;
          box-shadow: 0 2px 6px -2px rgba(15,23,42,0.08) !important;
          font-size: 18px !important;
          flex-shrink: 0 !important;
        }
        .rt-profile-modal .rt-modal-title {
          display: inline-flex !important;
          align-items: center !important;
          margin: 0 !important;
          color: #1e293b !important;
          font-size: 18px !important;
          font-weight: 500 !important;
          line-height: 1.2 !important;
          min-width: 0 !important;
        }
        .rt-profile-modal .rt-modal-title-text {
          display: inline-flex !important;
          align-items: center !important;
          gap: 12px !important;
          min-width: 0 !important;
          flex-wrap: wrap !important;
        }
        .rt-profile-modal .rt-modal-title-badge {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 3px 8px !important;
          height: 22px !important;
          box-sizing: border-box !important;
          border-radius: 0 !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.02em !important;
          background: #171717 !important;
          color: #ffffff !important;
          border: 1px solid #171717 !important;
          line-height: 1 !important;
        }
        .rt-profile-modal .rt-modal-meta-row {
          display: flex !important;
          align-items: center !important;
          flex-wrap: wrap !important;
          gap: 8px !important;
          margin-top: 0 !important;
          padding-left: 0 !important;
          font-size: 12px !important;
          font-weight: 400 !important;
          color: #64748b !important;
        }
        .rt-profile-modal .rt-meta-chip {
          display: inline-flex !important;
          align-items: center !important;
          gap: 4px !important;
          background: #ffffff !important;
          padding: 3px 8px !important;
          height: 24px !important;
          min-height: 24px !important;
          box-sizing: border-box !important;
          border-radius: 0 !important;
          font-size: 11.5px !important;
          font-weight: 500 !important;
          color: #0f172a !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: none !important;
          line-height: 1 !important;
        }
        .rt-profile-modal .rt-meta-chip i,
        .rt-profile-modal .rt-meta-chip .fa {
          font-size: 11.5px !important;
          margin-right: 4px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .rt-profile-modal .rt-meta-chip i.fa-user { color: #9333ea !important; }
        .rt-profile-modal .rt-meta-chip i.fa-envelope, .rt-profile-modal .rt-meta-chip i.fa-envelope-o { color: #0284c7 !important; }
        .rt-profile-modal .rt-meta-chip i.fa-phone { color: #16a34a !important; }
        .rt-profile-modal .rt-meta-chip i.fa-hashtag { color: #d97706 !important; }
        .rt-profile-modal .rt-meta-chip i.fa-folder-open { color: #2563eb !important; }
        .rt-profile-modal .rt-meta-chip i.fa-id-badge { color: #6366f1 !important; }

        .rt-profile-modal .rt-action-btn {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 32px !important;
          height: 32px !important;
          padding: 0 !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          color: #64748b !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          cursor: pointer !important;
          transition: all 0.15s ease !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
          outline: none !important;
          font-family: inherit !important;
        }
        .rt-profile-modal .rt-action-btn:hover {
          color: #dc2626 !important;
          border-color: #fca5a5 !important;
          background: #fef2f2 !important;
          transform: translateY(-1px) !important;
        }

        .rt-profile-modal .modal-body {
          padding: 20px 24px !important;
          background: #ffffff !important;
          max-height: calc(100vh - 200px) !important;
          overflow-y: auto !important;
        }
        .rt-profile-modal .modal-footer {
          background: #ffffff !important;
          border-top: 1px solid #e2e8f0 !important;
          padding: 14px 20px !important;
          display: flex !important;
          justify-content: flex-end !important;
          gap: 10px !important;
        }
        .rt-profile-modal .wf-modal-footer-close-btn {
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          padding: 7px 18px !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          color: #1e293b !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 0 !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
          outline: none !important;
          cursor: pointer !important;
          transition: all 0.15s ease !important;
          font-family: inherit !important;
        }
        .rt-profile-modal .wf-modal-footer-close-btn:hover {
          background: #f8fafc !important;
          color: #0f172a !important;
          border-color: #94a3b8 !important;
          transform: translateY(-1px) !important;
        }
        .rt-profile-modal .form-control,
        .rt-profile-modal select.form-control,
        .rt-profile-modal .input-group .form-control {
          border-radius: 0 !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: none !important;
        }
        .rt-profile-modal .form-control:focus,
        .rt-profile-modal .input-group .form-control:focus {
          border-color: #171717 !important;
          box-shadow: 0 0 0 1px #171717 !important;
        }

        .rt-password-requirements-card {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 0 !important;
          padding: 16px 20px !important;
        }
        .rt-password-requirements-card h3 {
          font-size: 13px !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          margin: 0 0 12px 0 !important;
          display: flex !important;
          align-items: center !important;
          gap: 6px !important;
        }
        .rt-password-requirements-card h3 i {
          color: #2563eb !important;
        }
        .rt-password-requirements-list {
          list-style: none !important;
          padding: 0 !important;
          margin: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 8px !important;
        }
        .rt-password-requirement-item {
          font-size: 12px !important;
          color: #64748b !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
        }
        .rt-password-requirement-item.valid {
          color: #0f172a !important;
          font-weight: 500 !important;
        }
        .rt-password-requirement-item.valid i {
          color: #16a34a !important;
        }
        .rt-password-submit-btn {
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          padding: 7px 18px !important;
          font-size: 13.5px !important;
          font-weight: 500 !important;
          color: #ffffff !important;
          background: #171717 !important;
          border: 1px solid #171717 !important;
          border-radius: 0 !important;
          cursor: pointer !important;
          transition: all 0.15s ease !important;
          outline: none !important;
        }
        .rt-password-submit-btn:hover:not(:disabled) {
          background: #333333 !important;
          border-color: #333333 !important;
          transform: translateY(-1px) !important;
        }
        .rt-password-submit-btn:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }
  `}</style>
        <Modal.Header>
          <div className="rt-header-shell">
            <div className="rt-header-top">
              <div className="rt-header-main">
                <i className="rt-header-icon fa fa-key" aria-hidden="true" />
                <div className="rt-header-copy">
                  <h2 className="rt-modal-title">
                    <span className="rt-modal-title-text">
                      <span>Change Password</span>
                      <span className="rt-modal-title-badge">
                        Security
                      </span>
                    </span>
                  </h2>
                  <div className="rt-modal-meta-row">
                    {user?.name && (
                      <span className="rt-meta-chip">
                        <i className="fa fa-user" aria-hidden="true" style={{ color: "#9333ea", marginRight: "4px" }} />
                        <span>{user.name}</span>
                      </span>
                    )}
                    {user?.id && (
                      <span className="rt-meta-chip">
                        <i className="fa fa-hashtag" aria-hidden="true" style={{ color: "#d97706", marginRight: "4px" }} />
                        <span>Reg ID: {user.id}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="rt-action-btn"
                onClick={handleModalShowHide}
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-7">
              <div className="row">
                <div className="col-md-12">
                  {renderInput("oldPassword", "Existing Password", "fa-lock")}
                </div>
                <div className="col-md-12">
                  {renderInput("newPassword", "New Password", "fa-key")}
                </div>
                <div className="col-md-12">
                  {renderInput("confirmPassword", "Confirm Password", "fa-shield")}
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="rt-password-requirements-card">
                <h3>
                  <i className="fa fa-shield" aria-hidden="true" />
                  Strength Requirements
                </h3>
                <ul className="rt-password-requirements-list">
                  <li className={`rt-password-requirement-item ${isLengthValid ? "valid" : ""}`}>
                    {renderCheckIcon(isLengthValid)}
                    <span>8 to 50 characters (no spaces)</span>
                  </li>
                  <li className={`rt-password-requirement-item ${isCapitalValid ? "valid" : ""}`}>
                    {renderCheckIcon(isCapitalValid)}
                    <span>At least one uppercase letter (A-Z)</span>
                  </li>
                  <li className={`rt-password-requirement-item ${isVowelValid ? "valid" : ""}`}>
                    {renderCheckIcon(isVowelValid)}
                    <span>At least one lowercase letter/vowel</span>
                  </li>
                  <li className={`rt-password-requirement-item ${isDigitValid ? "valid" : ""}`}>
                    {renderCheckIcon(isDigitValid)}
                    <span>At least one number (0-9)</span>
                  </li>
                  <li className={`rt-password-requirement-item ${isSpecialValid ? "valid" : ""}`}>
                    {renderCheckIcon(isSpecialValid)}
                    <span>At least one special character (e.g. !, @, #, ?, followed by letter/number)</span>
                  </li>
                  <li className={`rt-password-requirement-item ${isMatchValid ? "valid" : ""}`}>
                    {renderCheckIcon(isMatchValid)}
                    <span>Passwords must match</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="wf-modal-footer-close-btn"
            onClick={handleModalShowHide}
          >
            <i className="fa fa-times" aria-hidden="true" />
            Cancel
          </button>
          <button
            type="button"
            className="rt-password-submit-btn"
            disabled={validate()}
            onClick={() => handleSubmit()}
          >
            <i className="fa fa-check" aria-hidden="true" />
            Update Password
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default ChangePassword;
